import {
  ContentsUnion,
  DashboardInterface,
  LearningElementsInterface,
  LearningInterface,
  StatusInterface,
} from '@elearning-platform/api-types/interfaces';
import { DashboardContentUnionType } from '@elearning-platform/api-types/types';
import { Color, IconName, useNotifications, UseNotifications } from '@elearning-platform/ui';
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TransProps, useTranslation } from 'react-i18next';

import { addFavouriteItem, removeFavouriteItem } from '../api/requests';
import { FavouriteItemActionEnum, QueryKey } from '../enums';
import { ToggleFavouriteItemData, UseFavouriteItems } from '../interfaces';

export const useFavouriteItems: () => UseFavouriteItems = (): UseFavouriteItems => {
  const queryClient: QueryClient = useQueryClient();
  const { addToast }: UseNotifications = useNotifications();
  const { t }: TransProps<never> = useTranslation();

  const { mutateAsync: toggleFavouriteAsync }: UseMutationResult<StatusInterface, Error, ToggleFavouriteItemData> = useMutation({
    mutationFn: ({ type, id, action }: ToggleFavouriteItemData): Promise<StatusInterface> => action === FavouriteItemActionEnum.Add
      ? addFavouriteItem(type, id)
      : removeFavouriteItem(type, id),
    onSuccess: (_: StatusInterface, variables: ToggleFavouriteItemData): void => {
      queryClient.setQueryData(
        [QueryKey.KnowledgeBaseData],
        (oldData: LearningInterface | undefined): LearningInterface | undefined => oldData && ({
          ...oldData,
          elements: oldData.elements?.map((list: LearningElementsInterface): LearningElementsInterface => ({
            ...list,
            items: list.items?.map((item: ContentsUnion): ContentsUnion => item.id === variables.id
              ? { ...item, favorite: variables.action === FavouriteItemActionEnum.Add }
              : item
            ),
          })),
        }),
      );
      queryClient.setQueryData(
        [QueryKey.DashboardData],
        (oldData: DashboardInterface | undefined): DashboardInterface | undefined => oldData && ({
          ...oldData,
          content: {
            ...oldData.content,
            required: oldData.content.required.map((item: DashboardContentUnionType): DashboardContentUnionType => item.id === variables.id
              ? { ...item, favorite: variables.action === FavouriteItemActionEnum.Add }
              : item
            ),
          },
        }),
      );
      queryClient.setQueriesData(
        { queryKey: [QueryKey.PathRequestsEventsRequiringCompletion] },
        (oldData: ContentsUnion[] | undefined): ContentsUnion[] | undefined => oldData
          ?.map((item: ContentsUnion): ContentsUnion => item.id === variables.id
            ? { ...item, favorite: variables.action === FavouriteItemActionEnum.Add }
            : item
          )
      );
      addToast({
        icon: IconName.CheckmarkCircle,
        iconColor: Color.Green300,
        text: variables.action === FavouriteItemActionEnum.Add
          ? t('competence.app.useFavouriteItems.addedToList')
          : t('competence.app.useFavouriteItems.deletedFromList'),
      });
    },
    onError: (): void => addToast({
      icon: IconName.Error,
      iconColor: Color.Red300,
      text: t('competence.app.useFavouriteItems.error'),
    }),
  });

  return { toggleFavouriteAsync };
};
